import { INSERT_MOS_OBJECT_SUCCESS, MOS_OBJECT_PREVIEW_PATH_RETRIEVED } from '../actions/asset';
import {
  TIMELINE_GRAPHIC_LOSE_FOCUS,
  TIMELINE_REMOVE_GRAPHIC,
  TIMELINE_SELECT_GRAPHIC,
  TIMELINE_UPDATE_GRAPHIC_POSITION,
  VIDEO_JUMPED_TO_END,
  VIDEO_JUMPED_TO_START,
  VIDEO_LOADED,
  VIDEO_PAUSED,
  VIDEO_RESUMED,
  VIDEO_SCRUB_ENDED,
  VIDEO_SCRUB_STARTED,
  VIDEO_SCRUBBED,
  VIDEO_SELECTED,
  VIDEO_UPDATED
} from '../actions/clipperMovie';
import { getTimeCode } from '../util/timecode';

const initialState = {
  currentPosition: 0,
  currentTimeCode: '00:00:00.00',
  duration: '00:00:00.00',
  durationInMs: 0,
  fileUrl: '',
  frameRate: 30,
  isPlaying: false,
  isScrubbing: false,
  timelineGraphics: []
};

let nextGraphicId = 1;

const clipperMovie = (state = initialState, action) => {
  switch (action.type) {
    case INSERT_MOS_OBJECT_SUCCESS:
    case MOS_OBJECT_PREVIEW_PATH_RETRIEVED:
    case TIMELINE_GRAPHIC_LOSE_FOCUS:
    case TIMELINE_REMOVE_GRAPHIC: {
      return {
        ...state,
        timelineGraphics: timelineGraphics(state, action)
      };
    }
    case TIMELINE_SELECT_GRAPHIC: {
      return {
        ...state,
        isPlaying: false,
        timelineGraphics: timelineGraphics(state, action)
      };
    }
    case TIMELINE_UPDATE_GRAPHIC_POSITION: {
      const { position } = action.payload;
      return {
        ...state,
        currentPosition: position,
        currentTimeCode: getTimeCode(position, state.durationInMs, state.frameRate),
        isPlaying: false,
        timelineGraphics: timelineGraphics(state, action)
      };
    }
    case VIDEO_LOADED:
      return {
        ...state,
        duration: getTimeCode(1000, action.payload[0], state.frameRate),
        durationInMs: action.payload[0],
        isPlaying: false
      };
    case VIDEO_JUMPED_TO_END:
      return {
        ...state,
        currentPosition: 1000,
        currentTimeCode: getTimeCode(1000, state.durationInMs, state.frameRate),
        isPlaying: false,
        timelineGraphics: timelineGraphics(state, action)
      };
    case VIDEO_JUMPED_TO_START:
      return {
        ...state,
        currentPosition: initialState.currentPosition,
        currentTimeCode: initialState.currentTimeCode,
        isPlaying: false,
        timelineGraphics: timelineGraphics(state, action)
      };
    case VIDEO_PAUSED:
      return {
        ...state,
        isPlaying: false,
        timelineGraphics: timelineGraphics(state, action)
      };
    case VIDEO_RESUMED:
      return {
        ...state,
        isPlaying: true,
        timelineGraphics: timelineGraphics(state, action)
      };
    case VIDEO_SCRUB_ENDED:
      return {
        ...state,
        isScrubbing: false
      };
    case VIDEO_SCRUB_STARTED:
      return {
        ...state,
        isScrubbing: true
      };
    case VIDEO_SCRUBBED: {
      const position = parseInt(action.payload.position, 10);
      return {
        ...state,
        currentPosition: position,
        currentTimeCode: getTimeCode(position, state.durationInMs, state.frameRate),
        isPlaying: false,
        timelineGraphics: timelineGraphics(state, action)
      };
    }
    case VIDEO_SELECTED:
      return {
        ...state,
        fileUrl: action.payload.fileUrl,
        frameRate: action.payload.frameRate
      };
    case VIDEO_UPDATED: {
      const position = (action.payload.frame * 1000) / state.durationInMs;
      return {
        ...state,
        currentPosition: position,
        currentTimeCode: getTimeCode(position, state.durationInMs, state.frameRate),
        timelineGraphics: timelineGraphics(state, action)
      };
    }
    default:
      return state;
  }
};

/* state would normally be the timelineGraphics,
 * but properties of the rest of the clipperMovie need to be accessed.
 * So state is the clipperMovie, not timelineGraphics
*/
const timelineGraphics = (state = initialState, action) => {
  const durationInMs = state.durationInMs;
  const frameRate = state.frameRate;

  switch (action.type) {
    case INSERT_MOS_OBJECT_SUCCESS: {
      const exists = state.timelineGraphics.some(g => g.mosObjectId === action.payload.objId);

      if (exists) {
        return state.timelineGraphics.map(g => {
          if (g.mosObjectId === action.payload.objId) {
            return updateTimelineGraphic(g, action);
          }

          return {
            ...g,
            selected: false,
            focused: false
          };
        });
      }

      const timelineGraphic = createTimelineGraphic(state, action, durationInMs, frameRate);
      return [
        ...state.timelineGraphics.map(g => ({
          ...g,
          selected: false
        })),
        timelineGraphic
      ];
    }
    case MOS_OBJECT_PREVIEW_PATH_RETRIEVED:
      return state.timelineGraphics.map(g => {
        if (g.mosObjectId === action.payload.objId) {
          return {
            ...g,
            previewImgUrl: action.payload.previewImgUrl
          };
        }

        return g;
      });
    case TIMELINE_GRAPHIC_LOSE_FOCUS: {
      return [
        ...state.timelineGraphics.map(g => ({
          ...g,
          focused: false
        }))
      ];
    }
    case VIDEO_JUMPED_TO_END:
    case VIDEO_JUMPED_TO_START:
    case VIDEO_PAUSED:
    case VIDEO_RESUMED:
    case VIDEO_SCRUBBED:
    case VIDEO_UPDATED: {
      return state.timelineGraphics.map(g => ({
        ...g,
        selected: g.selected && g.mosXml != null && g.mosXml !== ''
      }));
    }
    case TIMELINE_REMOVE_GRAPHIC:
      return state.timelineGraphics.filter(g => !g.focused);
    case TIMELINE_SELECT_GRAPHIC:
      return state.timelineGraphics.map(g => ({
        ...g,
        selected: g.id === action.payload.graphicId,
        focused: g.id === action.payload.graphicId
      }));
    case TIMELINE_UPDATE_GRAPHIC_POSITION:
      return state.timelineGraphics.map(g => {
        if (g.id === action.payload.graphicId) {
          const start = action.payload.start;
          const end = action.payload.end;
          return {
            ...g,
            start,
            end,
            duration: getTimeCode(end - start, durationInMs, frameRate),
            focused: true,
            mosXml: updateMosXmlTimecode(g.mosXml, start, end, durationInMs, frameRate)
          };
        }

        return g;
      });
    default:
      return state.timelineGraphics;
  }
};

const updateMosXmlTimecode = (mosXml, start, end, durationInMs, frameRate) => {
  const startTimeCode = getTimeCode(start, durationInMs, frameRate);
  const startTimeCodeTag = `<sAVsom>${startTimeCode}</sAVsom>`;
  const duration = end - start;
  const durationTimeCode = getTimeCode(duration, durationInMs, frameRate);
  const durationTimeCodeTag = `<sAVeom>${durationTimeCode}</sAVeom>`;

  const updatedMosXml = mosXml
    .replace(/<sAVsom>.*<\/sAVsom>/g, startTimeCodeTag)
    .replace(/<sAVsom\/>/g, startTimeCodeTag)
    .replace(/<sAVeom>.*<\/sAVeom>/g, durationTimeCodeTag)
    .replace(/<sAVeom\/>/g, durationTimeCodeTag);

  return updatedMosXml;
};

const createTimelineGraphic = (state, action, durationInMs, frameRate) => {
  const start = state.currentPosition;
  let end = start + 100;
  end = Math.min(end, 1000);

  const timelineGraphic = {
    id: nextGraphicId,
    mosObjectId: action.payload.objId,
    mosXml: updateMosXmlTimecode(action.payload.mosXml, start, end, durationInMs, frameRate),
    title: action.payload.title,
    start,
    end,
    duration: getTimeCode(end - start, durationInMs, frameRate),
    selected: true,
    focused: true,
    replaceables: action.payload.replaceables,
    templateId: action.payload.templateId
  };

  nextGraphicId += 1;

  return timelineGraphic;
};

const updateTimelineGraphic = (original, action) => {
  const timelineGraphic = {
    ...original,
    mosXml: action.payload.mosXml,
    title: action.payload.title,
    selected: true,
    focused: true,
    replaceables: action.payload.replaceables,
    templateId: action.payload.templateId
  };

  return timelineGraphic;
};

export default clipperMovie;



// WEBPACK FOOTER //
// ./app/reducers/clipperMovie.js